import React from 'react';
import { I18nManager, Platform } from 'react-native';
import { Colors, Surface } from 'react-native-paper';
import { Trans } from '@lingui/macro';
import { useNavigation } from '@react-navigation/native';
import styled, { css } from 'styled-components/native';

import Image from '@src/components/Image';
import ImageBackground from '@src/components/ImageBackground';
import ImageIcon from '@src/components/ImageIcon';
import Touchable from '@src/components/Touchable';
import { Body1, Body2, Subtitle2 } from '@src/components/Typography';
import { windowWidth } from '@src/configuration/constants';
import { getTourBookings, padding } from '@src/configuration/constants';
import { durationTour, formatPrice, persianNumber } from '@src/helpers';
import { TourSummery } from '@src/types';

type Props = {
  item: TourSummery;
  isHome?: Boolean | undefined;
};
type SurfaceProps = {
  expanded?: boolean;
  isHome?: Boolean | undefined;
};
const ratioWidth = windowWidth * 0.67;
const ratioWidthHome = windowWidth * 0.72 - padding;
const ratioWidthDetail = windowWidth * 0.97 - 15;
const Container = styled(Surface)<SurfaceProps>`
  elevation: 2;
  border-radius: ${({ theme }) => theme.roundness}px;
  background-color: ${({ theme }) => theme.colors.placeholder};
  margin-bottom: ${padding / 2}px;
  margin-right: 8px;
  margin-left: 8px;
  width: ${({ isHome }) => (isHome ? ratioWidth : '100%')};
`;

const Wrapper = styled.View`
  border-radius: ${({ theme }) => theme.roundness}px;
  background-color: ${({ theme }) => theme.colors.background};
  overflow: hidden;
`;
const CoverContainer = styled.View<SurfaceProps>`
  border-radius: 16px;
  overflow: hidden;
  width: ${({ isHome }) => (isHome ? ratioWidth : '100%')};
  height: ${ratioWidth}px;
`;
const Cover = styled(ImageBackground)`
  ${Platform.select({
    ios: css`
      flex: 1;
    `,
    web: css`
      display: inline;
    `,
  })};
  margin: 0 auto;
  height: 100%;
  width: 100%;
`;
const Capacity = styled.View<SurfaceProps>`
  border-top-right-radius: 8px ;
  border-bottom-right-radius:8px
  background-color: ${({ theme }) => theme.colors.error};
  padding: 8px 8px 8px 8px;
  position: absolute;
  zindex: 1000;
  bottom: 20px;
  left: 0;
`;
const LargeText = styled(Body1).attrs({
  numberOfLines: 1,
  ellipsizeMode: 'tail',
  dir: 'unset',
})<{ weight?: number; padding?: number; isHome?: Boolean | undefined }>`
  padding-right: ${({ padding }) => (padding ? padding : 20)}px;
  font-weight: ${({ weight }) => (weight ? weight : 400)};
`;
export const NormalText = styled(Body2)<{
  textColor?: string;
  padding?: number;
}>`
  color: ${({ theme, textColor }) =>
    textColor ? textColor : theme.colors.onAccent};
  padding-left: ${({ padding }) => (padding ? padding : 0)}px;
`;
export const TextContainer = styled.View<{ isHome?: Boolean | undefined }>`
  width: ${({ isHome }) => (isHome ? ratioWidthHome : ratioWidthDetail)};
  flex-direction: row;
  padding-right: 20px;
  padding-bottom: 2px;
  flex-wrap: wrap;
`;
type BookingLabelProps = {
  booking: number;
};

const Content = styled.View`
  padding-left: 13px;
  padding-right: 8px;
  padding-top: ${padding / 2}px;
  padding-bottom: ${padding / 2}px;
  flex-direction: row;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.background};
`;

const ItemRow = styled.View<{ isHome?: Boolean | undefined }>`
  flex-direction: row;
  align-items: center;
  padding-right: ${({ isHome }) => (isHome ? 0 : 25)}px;
`;
const Row = styled.View<SurfaceProps>`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: ${({ isHome }) => (isHome ? ratioWidth - 22 : ratioWidthDetail)};
`;

const Line = styled.View`
  height: 16px;
  width: 1px;
  background-color: ${({ theme }) => theme.colors.placeholder};
`;
const Column = styled.View`
  align-items: flex-start;
`;
const IconView = styled.View`
  position: absolute;
  top: 8;
  left: 8;
  opacity: 1;
  z-index: 10;
  border-radius: 10px;
`;
const Component: React.FC<Props> = ({
  item: {
    cover,
    title,
    id,
    booking,
    price: rawPrice,
    leaderId,
    termIds,
    beginDate,
    endDate,
    operator,
    duration,
  },
  isHome,
}) => {
  const navigation = useNavigation();
  const handlePress = React.useCallback(
    () => navigation.navigate('Tour', { id }),
    [id, navigation]
  );

  const price = formatPrice(rawPrice);
  const specialTerm =
    termIds != undefined ? termIds?.filter((e) => e === 284).length : 0;
  const leaderId_ = persianNumber(leaderId);
  const source = cover?.uri
    ? { uri: cover?.uri }
    : require('@src/assets/images/tourPlaceholder.png');
  console.log(booking);
  return (
    <Container isHome={isHome}>
      <Touchable onPress={handlePress}>
        <Wrapper>
          {specialTerm > 0 && (
            <IconView>
              <ImageIcon
                source={require('@src/assets/images/tourPlus.png')}
                size={34}
              />
            </IconView>
          )}

          <CoverContainer>
            <Cover source={source} />
            {booking === 0 && (
              <Capacity>
                <NormalText textColor={'#ffffff'}>
                  <Trans>full</Trans>
                </NormalText>
              </Capacity>
            )}
          </CoverContainer>
          <Content>
            <Column>
              <TextContainer isHome={isHome}>
                <LargeText padding={2}>{persianNumber(title)}</LargeText>
              </TextContainer>
              <NormalText>
                {'(' + persianNumber(duration) + ')' || '-'}
              </NormalText>
              <ItemRow isHome={isHome}>
                <LargeText weight={500} padding={2}>
                  {price}
                </LargeText>
                <Subtitle2>
                  <Trans>Toman</Trans>
                </Subtitle2>
              </ItemRow>
              <Row isHome={isHome}>
                {isHome ? (
                  <NormalText textColor={'#757575'}>
                    {durationTour(beginDate, endDate)}
                  </NormalText>
                ) : (
                  <Subtitle2>{durationTour(beginDate, endDate)}</Subtitle2>
                )}
                <Line />
                <ItemRow isHome={isHome}>
                  <Subtitle2>
                    <Trans>Leader Code</Trans>
                  </Subtitle2>
                  <NormalText textColor={'#757575'} padding={7}>
                    {leaderId_}
                  </NormalText>
                </ItemRow>
              </Row>
            </Column>
          </Content>
        </Wrapper>
      </Touchable>
    </Container>
  );
};

Component.displayName = 'TourSummeryCard';

export default React.memo(Component);
